// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning a 40 KB blob of JSON into one line somebody can read.
 *
 * A card that dumps a workflow graph is not better than no card. The summary is
 * derived, never invented: it counts what is there and names what it found, and
 * where a field is absent it says nothing rather than filling in a default.
 */

import type { Recipe } from '../types.ts';
import { isComfyApiGraph, isComfyUiGraph } from './dialects.ts';

export interface A1111Fields {
  positive: string;
  negative: string;
  settings: Array<[string, string]>;
}

/**
 * The A1111 `parameters` grammar: the positive prompt, then an optional line
 * beginning "Negative prompt:", then a final comma-separated settings line.
 * Multi-line prompts are normal, so the settings line is identified as the LAST
 * line that looks like `Key: value, Key: value`.
 */
export function parseA1111(text: string): A1111Fields {
  const lines = text.split(/\r?\n/);
  let settingsIndex = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (line.length === 0) continue;
    if (/^[A-Za-z][\w .()/-]*:\s*[^,]*(,\s*[A-Za-z][\w .()/-]*:\s*[^,]*)+$/.test(line)) settingsIndex = i;
    break;
  }

  const settings: Array<[string, string]> = [];
  if (settingsIndex >= 0) {
    // Split on commas that are followed by a `Key:` — a prompt-shaped value such
    // as "Lora hashes: a, b" would otherwise be torn apart.
    for (const pair of lines[settingsIndex].split(/,\s*(?=[A-Za-z][\w .()/-]*:)/)) {
      const at = pair.indexOf(':');
      if (at < 0) continue;
      settings.push([pair.slice(0, at).trim(), pair.slice(at + 1).trim()]);
    }
  }

  const body = (settingsIndex >= 0 ? lines.slice(0, settingsIndex) : lines).join('\n');
  const negativeAt = body.search(/^Negative prompt:/m);
  if (negativeAt >= 0) {
    return {
      positive: body.slice(0, negativeAt).trim(),
      negative: body.slice(negativeAt).replace(/^Negative prompt:\s*/, '').trim(),
      settings,
    };
  }
  return { positive: body.trim(), negative: '', settings };
}

function comfyNodeTypes(value: unknown): { count: number; types: string[]; picked: Array<[string, string]> } {
  const picked: Array<[string, string]> = [];
  const counts = new Map<string, number>();

  const note = (key: string, raw: unknown) => {
    if (raw === undefined || raw === null || typeof raw === 'object') return;
    if (picked.some(([k]) => k === key)) return;
    picked.push([key, String(raw)]);
  };

  if (isComfyApiGraph(value)) {
    const nodes = Object.values(value as Record<string, { class_type: string; inputs?: Record<string, unknown> }>);
    for (const node of nodes) {
      counts.set(node.class_type, (counts.get(node.class_type) ?? 0) + 1);
      const inputs = node.inputs ?? {};
      if (/CheckpointLoader/i.test(node.class_type)) note('Model', inputs.ckpt_name);
      if (/UNETLoader|DiffusionModel/i.test(node.class_type)) note('Model', inputs.unet_name);
      if (/LoraLoader/i.test(node.class_type)) note('LoRA', inputs.lora_name);
      if (/KSampler|SamplerCustom/i.test(node.class_type)) {
        note('Sampler', inputs.sampler_name);
        note('Scheduler', inputs.scheduler);
        note('Steps', inputs.steps);
        note('CFG', inputs.cfg);
        note('Seed', inputs.seed ?? inputs.noise_seed);
      }
      if (/EmptyLatentImage|EmptySD3LatentImage/i.test(node.class_type)) {
        if (typeof inputs.width === 'number' && typeof inputs.height === 'number') {
          note('Size', `${inputs.width} x ${inputs.height}`);
        }
      }
    }
    return { count: nodes.length, types: [...counts.keys()], picked };
  }

  if (isComfyUiGraph(value)) {
    const nodes = (value as { nodes: Array<{ type?: string }> }).nodes;
    for (const node of nodes) if (typeof node.type === 'string') counts.set(node.type, (counts.get(node.type) ?? 0) + 1);
    return { count: nodes.length, types: [...counts.keys()], picked };
  }

  return { count: 0, types: [], picked };
}

export interface RecipeSummary {
  headline: string;
  fields: Array<[string, string]>;
}

export function summariseRecipe(recipe: Recipe): RecipeSummary {
  if (recipe.kind === 'json') {
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(recipe.text);
    } catch {
      return { headline: `${recipe.byteLength.toLocaleString()} bytes that did not parse as JSON`, fields: [] };
    }
    const graph = comfyNodeTypes(parsed);
    if (graph.count > 0) {
      const shown = graph.types.slice(0, 4).join(', ');
      const more = graph.types.length > 4 ? ` and ${graph.types.length - 4} more` : '';
      return {
        headline: `${graph.count} nodes — ${shown}${more}`,
        fields: graph.picked,
      };
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const record = parsed as Record<string, unknown>;
      const fields: Array<[string, string]> = [];
      for (const key of Object.keys(record).slice(0, 8)) {
        const value = record[key];
        if (value === null || typeof value === 'object') continue;
        fields.push([key, String(value).slice(0, 160)]);
      }
      return { headline: `${Object.keys(record).length} fields`, fields };
    }
    return { headline: `${recipe.byteLength.toLocaleString()} bytes of JSON`, fields: [] };
  }

  const a1111 = parseA1111(recipe.text);
  if (a1111.settings.length > 0 || a1111.positive.length > 0) {
    const keep = ['Steps', 'Sampler', 'CFG scale', 'Seed', 'Size', 'Model', 'Model hash', 'Schedule type'];
    const fields = a1111.settings.filter(([k]) => keep.includes(k));
    return {
      headline: a1111.positive.slice(0, 200) || 'no prompt line',
      fields: fields.length > 0 ? fields : a1111.settings.slice(0, 6),
    };
  }
  return { headline: recipe.text.slice(0, 200), fields: [] };
}

export interface SensitiveTerm {
  kind: string;
  sample: string;
}

/**
 * Things in a prompt that its author probably did not mean to publish.
 *
 * Deliberately narrow: only patterns with a shape, never a guess about whether a
 * capitalised phrase is somebody's name. A false alarm here teaches the user to
 * ignore the warning, which is worse than not warning at all.
 */
export function flagSensitiveTerms(text: string): SensitiveTerm[] {
  const found: SensitiveTerm[] = [];
  const seen = new Set<string>();
  const add = (kind: string, sample: string) => {
    const key = `${kind}:${sample}`;
    if (seen.has(key) || found.length >= 12) return;
    seen.add(key);
    found.push({ kind, sample: sample.length > 90 ? `${sample.slice(0, 87)}…` : sample });
  };

  for (const m of text.matchAll(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g)) {
    add('an email address', m[0]);
  }
  for (const m of text.matchAll(/https?:\/\/[^\s"'<>\\]+/g)) add('a web address', m[0]);
  for (const m of text.matchAll(/\b[A-Za-z]:\\\\?[^\s"'<>|]*/g)) add('a path on a Windows machine', m[0]);
  for (const m of text.matchAll(/(?:\/Users|\/home|\/Volumes|\/mnt|\/media)\/[^\s"'<>,)\]]+/g)) {
    add('a path on your computer', m[0]);
  }
  for (const m of text.matchAll(/<lora:([^:>]+)[^>]*>/g)) add('a LoRA filename', m[1]);
  return found;
}
